import { parseFontCsv, loadFontFromUrl } from '../utils/fontUtils';

class FontService {
  constructor() {
    this.loadingPromise = null;
    this.fonts = [];
    this.isLoading = false;
    this.loadError = null;
    this.initialFontsLoaded = false;
    this.fontLoadingQueue = Promise.resolve(); // Queue for sequential font loading
  }

  /**
   * Load only essential fonts from a CSV URL (first few fonts)
   * @param {string} csvUrl - URL to the CSV file containing font information
   * @returns {Promise<Array>} - Promise that resolves with the loaded fonts array
   */
  async loadFonts(csvUrl) {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.isLoading = true;
    this.loadError = null;

    // Create and store the loading promise
    this.loadingPromise = (async () => {
      try {
        // Parse the CSV to get font information
        const allFonts = await parseFontCsv(csvUrl);
        
        if (!allFonts || allFonts.length === 0) {
          console.warn('No fonts found in the CSV');
          this.isLoading = false;
          return [];
        }

        // Store all font information but mark them as not loaded yet
        this.fonts = allFonts.map(font => ({
          ...font,
          loaded: false
        }));
        
        // Skip preloading - fonts will be loaded on-demand when needed
        // All fonts start as not loaded for on-demand loading
        console.log('📝 FontService: Loaded font metadata without preloading - using system fonts for UI');
        console.log(`📝 Available fonts: ${this.fonts.length} fonts ready for on-demand loading`);
        
        const loadedCount = 0; // No fonts preloaded
        
        this.initialFontsLoaded = true;
        
        return this.fonts;
      } catch (error) {
        console.error('Error loading fonts:', error);
        this.loadError = error;
        throw error;
      } finally {
        this.isLoading = false;
        // Clear promise reference after completion for future retries
        setTimeout(() => {
          this.loadingPromise = null;
        }, 0);
      }
    })();
    
    return this.loadingPromise;
  }
  
  /**
   * Load a specific font on demand when it's needed
   * @param {string} fontName - Name of the font to load
   * @returns {Promise<boolean>} - Promise that resolves to true if font loaded successfully
   */
  async loadFontOnDemand(fontName) {
    // If font name is not provided, return false
    if (!fontName) {
      console.warn('No font name provided for on-demand loading');
      return false;
    }
    
    // Find the font in our list
    const fontInfo = this.fonts.find(f => f.name === fontName);
    if (!fontInfo) {
      console.warn(`Font ${fontName} not found in available fonts list`);
      return false;
    }
    
    // If font is already loaded, return true immediately
    if (fontInfo.loaded) {
      
      return true;
    }
    
    
    
    // Add to loading queue to prevent multiple simultaneous font loads
    // which can cause performance issues
    this.fontLoadingQueue = this.fontLoadingQueue.then(async () => {
      try {
        const success = await loadFontFromUrl(fontInfo.name, fontInfo.url);
        
        // Update font status in our list
        if (success) {
          const fontIndex = this.fonts.findIndex(f => f.name === fontName);
          if (fontIndex >= 0) {
            this.fonts[fontIndex].loaded = true;
          }
          
        } else {
          console.error(`Failed to load font ${fontName} on demand`);
        }
        
        return success;
      } catch (error) {
        console.error(`Error loading font ${fontName} on demand:`, error);
        return false;
      }
    });
    
    return this.fontLoadingQueue;
  }

  /**
   * Get a list of all available fonts
   * @returns {Array} - Array of font objects
   */
  getFonts() {
    return this.fonts;
  }

  /**
   * Get a specific font by name
   * @param {string} fontName - Name of the font to get
   * @returns {Object|null} - Font object or null if not found
   */
  getFont(fontName) {
    return this.fonts.find(font => font.name === fontName) || null;
  }

  /**
   * Check if a specific font is loaded
   * @param {string} fontName - Name of the font to check
   * @returns {boolean} - True if font is loaded, false otherwise
   */
  isFontLoaded(fontName) {
    const font = this.getFont(fontName);
    return font ? font.loaded : false;
  }

  /**
   * Check if initial fonts are loaded
   * @returns {boolean} - True if initial fonts are loaded
   */
  areInitialFontsLoaded() {
    return this.initialFontsLoaded;
  }

  /**
   * Get loading status
   * @returns {boolean} - True if fonts are currently loading
   */
  isLoadingFonts() {
    return this.isLoading;
  }

  /**
   * Get any loading error
   * @returns {Error|null} - Error object or null if no error
   */
  getLoadError() {
    return this.loadError;
  }
  
  /**
   * Reset the font service state
   * This is useful when switching products or reloading the app
   */
  reset() {
    this.loadingPromise = null;
    this.fonts = [];
    this.isLoading = false;
    this.loadError = null;
    this.initialFontsLoaded = false;
    this.fontLoadingQueue = Promise.resolve();
    
  }

  /**
   * Get the current loading state
   * @returns {Object} - Object containing loading state and loaded fonts
   */
  getLoadingState() {
    return {
      isLoading: this.isLoading,
      fonts: this.fonts,
      error: this.loadError,
      allFontsLoaded: !this.isLoading && this.loadError === null && this.fonts.length > 0
    };
  }

  /**
   * Get a font by name
   * @param {string} name - Name of the font to find
   * @returns {Object|null} - Font object or null if not found
   */
  getFontByName(name) {
    if (!name || !this.fonts || this.fonts.length === 0) return null;
    return this.fonts.find(font => font.name === name) || null;
  }

  /**
   * Check if all fonts are loaded
   * @returns {boolean} - True if all fonts are loaded
   */
  areAllFontsLoaded() {
    return !this.isLoading && 
           this.loadError === null && 
           this.fonts.length > 0 && 
           this.fonts.every(font => font.loaded);
  }
}

// Export a singleton instance
const fontService = new FontService();
export default fontService;
